/**
 * JWT Token Storage Utilities
 * JWT 토큰 저장 및 관리
 *
 * ⚠️ 보안 경고 (SECURITY WARNING)
 * ===============================
 * 현재 구현은 localStorage를 사용하며, XSS 공격에 취약합니다.
 * 악의적인 스크립트가 페이지에 삽입될 경우 토큰이 노출될 수 있습니다.
 *
 * 권장 해결책:
 * 1. HttpOnly 쿠키 사용 (백엔드 변경 필요)
 *    - Django: response.set_cookie(key, value, httponly=True, secure=True, samesite='Strict')
 *    - Frontend: 자동으로 Authorization 헤더 대신 쿠키 사용
 *
 * 2. 토큰 갱신 흐름 개선
 *    - Access Token: 메모리에만 저장 (짧은 수명, 예: 15분)
 *    - Refresh Token: HttpOnly 쿠키에 저장
 *
 * 3. CSRF 보호
 *    - SameSite 쿠키 속성 사용
 *    - CSRF 토큰 추가
 *
 * TODO: Phase 18 또는 그 이후에 HttpOnly 쿠키 방식으로 마이그레이션 필요
 * - 백엔드: JWT를 HttpOnly 쿠키로 설정
 * - 프론트엔드: withCredentials: true 사용
 * - API 클라이언트: Authorization 헤더 제거, 쿠키 자동 전송 활용
 *
 * 현재 완화 조치:
 * - Content Security Policy (CSP) 헤더 설정 (백엔드/인프라)
 * - 입력 검증 및 출력 이스케이프 (XSS 방지)
 * - 토큰 만료 시간 짧게 설정 (24시간)
 */

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Store access token in localStorage
 *
 * ⚠️ 보안 주의: localStorage는 XSS 공격에 취약합니다.
 * 프로덕션 환경에서는 HttpOnly 쿠키 사용을 권장합니다.
 *
 * @see 파일 상단의 보안 경고 참조
 */
export function setAccessToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEY, token);
  }
}

/**
 * Store refresh token in cookies
 */
export function setRefreshToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }
}

/**
 * Get access token from cookies
 */
export function getAccessToken(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(TOKEN_KEY);
  }
  return null;
}

/**
 * Get refresh token from cookies
 */
export function getRefreshToken(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }
  return null;
}

/**
 * Store both access and refresh tokens
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
}

/**
 * Clear all tokens
 */
export function clearTokens(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

/**
 * Check if token exists
 */
export function hasAccessToken(): boolean {
  return getAccessToken() !== null;
}

/**
 * Decode JWT token (간단한 디코딩)
 */
export function decodeToken(token: string): Record<string, string | number | boolean | null> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {return null;}

    const payload = parts[1];
    const decoded = JSON.parse(Buffer.from(payload, 'base64').toString());
    return decoded;
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || typeof decoded.exp !== 'number') {return true;}

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  return Date.now() >= expirationTime;
}
